import React from 'react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { CHART_COLORS } from '@/lib/constants';

export function TimeSeriesChart({
  data = [],
  title = 'Scans Over Time',
  loading = false,
  showUnique = true,
  totalLabel = 'Total Scans',
  uniqueLabel = 'Unique Scans',
  // Multi-source mode: [{ key, label }]. Each key is a field on every data
  // point; one stacked area per source, named in the legend.
  series = null,
  description,
  className,
}) {
  if (loading) {
    return (
      <Card className={className}>
        <CardHeader>
          <Skeleton className="h-5 w-32" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-64 w-full" />
        </CardContent>
      </Card>
    );
  }

  const multi = Array.isArray(series) && series.length > 0;

  // Format data for recharts
  const chartData = multi
    ? data.map((point) => ({ ...point, date: point.period || point.date }))
    : data.map((point) => ({
      date: point.period || point.date,
      total: point.total_scans || point.total || 0,
      unique: point.unique_scans || point.unique || 0,
    }));

  return (
    <Card className={className} data-testid="timeseries-chart">
      <CardHeader>
        <CardTitle className="text-base font-medium">{title}</CardTitle>
        {description && <p className="text-sm text-muted-foreground">{description}</p>}
      </CardHeader>
      <CardContent>
        <div className="h-64" data-testid="analytics-timeseries-chart">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData}>
              <defs>
                <linearGradient id="colorTotal" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.32} />
                  <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0.02} />
                </linearGradient>
                <linearGradient id="colorUnique" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="hsl(var(--accent))" stopOpacity={0.32} />
                  <stop offset="100%" stopColor="hsl(var(--accent))" stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" />
              <XAxis
                dataKey="date"
                tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }}
                tickLine={{ stroke: 'hsl(var(--border))' }}
              />
              <YAxis
                tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }}
                tickLine={{ stroke: 'hsl(var(--border))' }}
              />
              <Tooltip
                contentStyle={{
                  background: 'hsl(var(--card))',
                  border: '1px solid hsl(var(--border))',
                  borderRadius: '8px',
                }}
              />
              <Legend />
              {multi ? (
                series.map((sr, i) => (
                  <Area
                    key={sr.key}
                    type="monotone"
                    dataKey={sr.key}
                    name={sr.label}
                    stackId="sources"
                    stroke={CHART_COLORS[i % CHART_COLORS.length]}
                    fill={CHART_COLORS[i % CHART_COLORS.length]}
                    fillOpacity={0.25}
                    strokeWidth={2}
                  />
                ))
              ) : (
                <>
                  <Area
                    type="monotone"
                    dataKey="total"
                    name={totalLabel}
                    stroke="hsl(var(--primary))"
                    fill="url(#colorTotal)"
                    strokeWidth={2}
                  />
                  {showUnique && (
                    <Area
                      type="monotone"
                      dataKey="unique"
                      name={uniqueLabel}
                      stroke="hsl(var(--accent))"
                      fill="url(#colorUnique)"
                      strokeWidth={2}
                    />
                  )}
                </>
              )}
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
}

export default TimeSeriesChart;
